// ─────────────────────────────────────────────────────────────────────────────
// services/printerKeyService.ts — DIE DREI SCHREIBWEGE DER OBERFLAECHE GEHEN
// UEBER DAS SPERRDOKUMENT `printerKeys/{payfixPrinterId}` (T-M41.8,
// Dashboard-Teil, 2026-09-18)
//
// Spec: **REQ-DM-036 Praez. 4** (die Eindeutigkeit traegt ein Sperrdokument;
// Nachfuehrungen (i)–(x)), **REQ-OV-008 Praez. 2** (`printerKeys` und seine
// drei Feldnamen sind feste Namen), **REQ-DM-035** (die Kennung ist der EINZIGE
// Aufloesungsschluessel des Alt-Eingangs), **REQ-SEC-034** (die Vorab-Auskunft
// bleibt, sie wird NICHT ersetzt), **REQ-SEC-005** (die Archivierung der
// REST-API ist kein Loeschweg), **REQ-OPS-038** (eine Quelle der
// Kanonisierung), **REQ-SEC-011/-029** (die Rules entscheiden, nicht der
// Client).
//
// ═════════════════════════════════════════════════════════════════════════════
// WARUM ES DIESE DATEI GIBT — OP-125, und der Befund ist aelter als der Bau
//
//   `payfixPrinterId` ist systemweit eindeutig, weil der Alt-Eingang sie OHNE
//   Mandantenkenntnis aufloest. Geprueft wurde das an drei Stellen und an
//   keiner atomar: `POST`/`PUT /v1/printers` fragten vor dem Schreiben ab, die
//   Verwaltungsoberflaeche fragt seit T-M30.10 die Cloud (`checkpayfixprinterid`,
//   REQ-SEC-034). **Zwischen Abfrage und Schreibvorgang liegt jedes Mal ein
//   Fenster. Es fehlte die GARANTIE, nicht die Pruefung.**
//
//   ⚠ EINE TRANSAKTION MIT ABFRAGE IM LESESATZ GENUEGT NICHT — und das ist der
//   Grund der Bauform: **das Firebase Web SDK kennt KEINE Abfrage innerhalb
//   einer Transaktion**, nur Einzeldokument-Lesevorgaenge (REQ-DM-036
//   Praez. 4 (b), am SDK gemessen). Ein Weg, der nur die Cloud absichert,
//   liesse genau den Schreiber ungeschuetzt, aus dem der reale Fall stammt:
//   zwei Bediener erfassen dieselbe Portal-Kennung. **Die DOKUMENT-ID traegt
//   die Eindeutigkeit**, und ein Einzeldokument-Lesevorgang genuegt.
// ═════════════════════════════════════════════════════════════════════════════
//
// ── WAS DIE RULES TRAGEN (`firebase/firestore.rules`, NUR GELESEN) ──────────
//   • `get` gelingt einem Verwaltungsnutzer auf ein NICHT bestehendes Dokument
//     (`resource == null`) — **das ist der Regelfall dieser Datei**, nicht der
//     Sonderfall; darauf beruht die Pruefung in der Transaktion.
//   • Ein BESTEHENDES Sperrdokument liest nur, wer seinen Mandanten kennen
//     darf (Admin oder Mitglied). **Ein FREMDES ist nicht lesbar** — der
//     Lesevorgang endet mit `permission-denied`, und das ist gewollt
//     (REQ-SEC-034 (c)): der Bediener erfaehrt DASS die Kennung belegt ist,
//     nicht WER sie belegt.
//   • `create` nur auf ein nicht bestehendes Dokument, Feldmenge ABSCHLIESSEND
//     `{ printerId, companyId, createdAt }` (`hasOnly`), `companyId` muss die
//     des Druckers sein. **Ein viertes Feld wird abgewiesen.**
//   • **`update` GIBT ES NICHT.** Ein Kennungswechsel ist `delete`-alt +
//     `create`-neu im SELBEN Commit — genau darin liegt die Garantie.
//   • `list` ist zu. Die Sammlung wird ausschliesslich ueber die Dokument-ID
//     angesprochen; es entsteht **keine neue Abfrageform** (REQ-DM-050a).
//
// ── DIE DREI WEGE, UND WAS SIE UNTERSCHEIDET ────────────────────────────────
//   ANLEGEN   Drucker UND Sperrdokument in EINER Transaktion. Besteht das
//             Sperrdokument, entsteht KEIN Drucker.
//   AENDERN   Kennung unveraendert → fehlt das Sperrdokument, wird es ANGELEGT
//             (der Uebergangsfall bis zur Bestandswanderung T-M41.11 heilt an
//             genau dem Drucker, den jemand ohnehin anfasst — dieselbe Regel
//             wie im `PUT` der Cloud). Kennung gewechselt → das ALTE
//             Sperrdokument wird im Lesesatz GELESEN und nur dann geloescht,
//             wenn es DIESEM Drucker gehoert.
//   LOESCHEN  Drucker UND sein Sperrdokument in EINEM Commit. Ein FEHLENDES
//             Sperrdokument ist KEIN Fehler; ein FREMDES wird nicht angefasst.
//
// ⚠ EINE ARCHIVIERUNG RAEUMT NIE AB (Entscheid des Auftraggebers vom
//   2026-09-18, woertlich „Nur echtes Loeschen gibt frei"; REQ-DM-036 Praez. 4
//   Nachf. (i)/(ii)). **Die Verwaltungsoberflaeche kennt ueberhaupt keinen
//   Archivierungsweg** — sie schreibt `archived` an keiner Stelle; die
//   Archivierung ist allein `DELETE /v1/printers/:id` der REST-API
//   (REQ-SEC-005) und liegt damit ausserhalb dieser Datei. Der einzige ECHTE
//   Loeschweg des Systems ist {@link loescheDruckerMitSperrdokument}, gerufen
//   aus `views/PrintersView.vue` ueber `printerStore.deletePrinter`.
//
// ── EINE GRENZE, BENANNT STATT SPAETER NEU ENTDECKT ────────────────────────
//   Ein Drucker OHNE `payfixPrinterId` bekommt KEIN Sperrdokument — er belegt
//   keine Kennung und kann keine verbrennen. Das Formular verlangt die Kennung
//   an beiden Ausgabearten (REQ-DM-035), ein ueber die Schnittstelle angelegter
//   Drucker traegt sie ebenfalls; der Zweig bleibt trotzdem stehen, weil ein
//   Bestandsdokument sie fuehren kann, ohne dass sie lesbar waere (Fremdtyp).
// ─────────────────────────────────────────────────────────────────────────────

import {
  addDoc, collection, doc, runTransaction, serverTimestamp,
  type DocumentData, type DocumentSnapshot, type Transaction,
} from 'firebase/firestore';
import { db } from '@/firebase/config';
// ⚠ DIE KANONISIERUNG UND DIE BELEGT-MELDUNG STEHEN IM REINEN MODUL, NICHT
// HIER (REQ-OPS-038): `utils/payfixPrinterId.ts` kennt weder Vue noch Firestore
// und ist damit ohne Mount und ohne Netz pruefbar. Die Fehlerklasse liegt aus
// demselben Grund dort — die Ansicht prueft sie mit `instanceof` und darf sich
// dafuer keinen Firebase-Weg einhandeln.
import { payfixPrinterIdDocKey, PayfixPrinterIdBelegtError } from '@/utils/payfixPrinterId';

export { PayfixPrinterIdBelegtError };

/**
 * Die Sammlung der Sperrdokumente — **fester Name** (REQ-OV-008 Praez. 2).
 * Sie steht hier als EINE Konstante und nicht als Literal an vier Stellen:
 * `printerKeys` ist eine Vertragsflaeche zwischen der REST-API, dieser
 * Oberflaeche, `firestore.rules` und dem Wanderungsskript.
 */
export const PRINTER_KEYS_COLLECTION = 'printerKeys';

/**
 * Die drei Felder des Sperrdokuments — **abschliessend** (REQ-DM-036
 * Praez. 4 (viii), REQ-OV-008 Praez. 2). Die Rules setzen die Menge mit
 * `hasOnly` durch; ein viertes Feld wird abgewiesen, nicht ignoriert.
 */
function sperrdokumentDaten(printerId: string, companyId: string): DocumentData {
  return { printerId, companyId, createdAt: serverTimestamp() };
}

/** Liest die Drucker-Kennung aus einem Sperrdokument; `''`, wenn sie fehlt. */
function druckerAusSperrdokument(snap: DocumentSnapshot): string {
  const roh = snap.data()?.printerId;
  return typeof roh === 'string' ? roh : '';
}

/** Liest den Mandanten aus einem Sperrdokument; `''`, wenn er fehlt. */
function mandantAusSperrdokument(snap: DocumentSnapshot): string {
  const roh = snap.data()?.companyId;
  return typeof roh === 'string' ? roh : '';
}

/** Eine getrimmte Zeichenkette aus einem Rohwert; jeder Fremdtyp ergibt `''`. */
function text(roh: unknown): string {
  return typeof roh === 'string' ? roh.trim() : '';
}

/**
 * Die Kennung, unter der ein Dokument sein Sperrdokument fuehrt — oder `''`.
 *
 * ⚠ EINE UNZULAESSIGE KENNUNG ERGIBT HIER `''` UND KEINEN FEHLER. Am
 * Schreibweg der Oberflaeche ist sie bereits abgewiesen (Formularpruefung,
 * REQ-DM-036 Praez. 4 (ix)); an einem BESTANDSDOKUMENT kann sie trotzdem
 * stehen, und dort heisst sie schlicht „hat kein Sperrdokument" — dieselbe
 * Behandlung wie in der Cloud (`sperrdokumentSchluessel`).
 */
function schluesselOderLeer(roh: unknown): string {
  const ergebnis = payfixPrinterIdDocKey(roh);
  return 'key' in ergebnis ? ergebnis.key : '';
}

/** Die Referenz auf ein Sperrdokument. */
function sperrRef(schluessel: string) {
  return doc(db, PRINTER_KEYS_COLLECTION, schluessel);
}

/** Hat die Ablehnung den Firestore-Code `permission-denied`? */
function istZugriffVerweigert(err: unknown): boolean {
  return typeof (err as { code?: unknown } | null)?.code === 'string'
    && (err as { code: string }).code === 'permission-denied';
}

/**
 * Liest das Sperrdokument der ZU BELEGENDEN Kennung im Lesesatz einer
 * Transaktion.
 *
 * ⚠ **EINE VERWEIGERTE LESUNG IST HIER DIE BELEGUNG UND KEIN FEHLER.** Die
 * Rules lassen ein fremdes bestehendes Sperrdokument nicht lesen
 * (REQ-SEC-034 (c)); dass die Lesung scheitert, sagt genau zweierlei — das
 * Dokument besteht, und es gehoert nicht uns.
 */
async function leseSperre(tx: Transaction, ref: ReturnType<typeof sperrRef>) {
  try {
    return await tx.get(ref);
  } catch (err: unknown) {
    if (!istZugriffVerweigert(err)) throw err;
    throw new PayfixPrinterIdBelegtError({ fremd: true });
  }
}

/**
 * Liest das Sperrdokument der FREIZUGEBENDEN (alten) Kennung — still.
 *
 * Ist es nicht lesbar, gehoert es nicht diesem Drucker; dann wird es ohnehin
 * nicht freigegeben, und der Aenderungs- oder Loeschweg darf daran nicht
 * scheitern.
 */
async function leseSperreStill(tx: Transaction, ref: ReturnType<typeof sperrRef>) {
  try {
    return await tx.get(ref);
  } catch (err: unknown) {
    if (!istZugriffVerweigert(err)) throw err;
    return null as DocumentSnapshot | null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. ANLEGEN — Drucker UND Sperrdokument in EINER Transaktion
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Legt einen Drucker an und belegt seine Kennung im selben Commit
 * (REQ-DM-036 Praez. 4 (c)).
 *
 * ⚠ **BESTEHT DAS SPERRDOKUMENT, ENTSTEHT KEIN DRUCKER.** Das ist die
 * Zusicherung, um derentwillen der Task besteht: die Vorab-Auskunft
 * (REQ-SEC-034) bleibt und nennt die Firma — aber sie ist nicht atomar, und
 * eine zwischen Auskunft und Schreibvorgang entstandene Belegung laesst den
 * Schreibvorgang hier scheitern, statt eine zweite Kennung entstehen zu lassen.
 *
 * @throws {PayfixPrinterIdBelegtError} wenn die Kennung belegt ist
 * @returns die Dokument-Kennung des angelegten Druckers
 */
export async function legeDruckerMitSperrdokumentAn(
  data: DocumentData,
): Promise<string> {
  const schluessel = schluesselOderLeer(data.payfixPrinterId);
  const inhalt     = { ...data, createdAt: serverTimestamp() };

  // Ohne Kennung gibt es nichts zu belegen — unveraenderter Weg.
  if (!schluessel) {
    const ref = await addDoc(collection(db, 'printers'), inhalt);
    return ref.id;
  }

  // `doc(collection(...))` erzeugt die Kennung LOKAL, ohne Schreibvorgang —
  // deshalb kann sie im Sperrdokument stehen, bevor der Drucker existiert.
  const printerRef = doc(collection(db, 'printers'));
  const companyId  = text(data.companyId);
  const ref        = sperrRef(schluessel);

  await runTransaction(db, async (tx) => {
    // ── Lesesatz ────────────────────────────────────────────────────────────
    // Ein `get` auf ein NICHT bestehendes Dokument GELINGT (die Rules erlauben
    // es ausdruecklich, `resource == null`) — darauf beruht diese Pruefung.
    const sperre = await leseSperre(tx, ref);
    if (sperre.exists()) {
      throw new PayfixPrinterIdBelegtError({
        fremd:             false,
        belegenderDrucker: druckerAusSperrdokument(sperre),
        belegenderMandant: mandantAusSperrdokument(sperre),
      });
    }
    // ── Schreibsatz ─────────────────────────────────────────────────────────
    tx.set(printerRef, inhalt);
    tx.set(ref, sperrdokumentDaten(printerRef.id, companyId));
  });

  return printerRef.id;
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. AENDERN — das Sperrdokument WANDERT im selben Commit
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Aendert einen Drucker und fuehrt sein Sperrdokument nach (REQ-DM-036
 * Praez. 4 (d)).
 *
 * DREI LAGEN, und sie sind auseinanderzuhalten:
 *  (a) **Die Aenderung NENNT die Kennung nicht** (Teiländerung, etwa die
 *      Schnellzuweisung eines Druckservers oder der Anzeige-Schluessel): die
 *      Kennung des gespeicherten Dokuments gilt weiter. Es wird nichts
 *      freigegeben — **fehlt das Sperrdokument, wird es angelegt.**
 *  (b) **Die Kennung bleibt gleich:** wie (a).
 *  (c) **Die Kennung wechselt:** das ALTE Sperrdokument wird im Lesesatz
 *      gelesen und nur dann geloescht, wenn es DIESEM Drucker gehoert; das
 *      neue wird angelegt. Danach ist die alte Kennung wieder frei.
 *
 * ⚠ DAS ALTE SPERRDOKUMENT WIRD GELESEN, BEVOR ES GELOESCHT WIRD. Ein blindes
 * `delete` gaebe eine Kennung frei, die inzwischen einem ANDEREN Drucker
 * gehoert — dieser Schreibvorgang verbrennte dann die Garantie eines Dritten.
 *
 * @throws {PayfixPrinterIdBelegtError} wenn die NEUE Kennung fremd belegt ist
 */
export async function aendereDruckerMitSperrdokument(
  printerId: string,
  update: DocumentData,
): Promise<void> {
  const printerRef  = doc(db, 'printers', printerId);
  // Nennt die Aenderung die Kennung ueberhaupt? `undefined` ist hier NICHT
  // gleichbedeutend mit „entfernen": eine Teiländerung schreibt das Feld nicht
  // mit, und sie darf die Kennung nicht freigeben.
  const nenntKennung = Object.prototype.hasOwnProperty.call(update, 'payfixPrinterId');

  await runTransaction(db, async (tx) => {
    // ── Lesesatz (in einer Transaktion stehen ALLE Lesevorgaenge vor jedem
    //    Schreibvorgang) ────────────────────────────────────────────────────
    const printer     = await tx.get(printerRef);
    const gespeichert = schluesselOderLeer(printer.data()?.payfixPrinterId);
    const neu         = nenntKennung ? schluesselOderLeer(update.payfixPrinterId) : gespeichert;
    // Der Mandant des Sperrdokuments ist der des DRUCKERS (die Rules pruefen
    // genau das). Die Aenderung hat den Vorrang, wenn sie ihn nennt.
    const companyId = text(
      'companyId' in update ? update.companyId : printer.data()?.companyId,
    );

    let neuRef: ReturnType<typeof sperrRef> | null = null;
    let neuBesteht = false;
    if (neu) {
      neuRef = sperrRef(neu);
      const neuSnap = await leseSperre(tx, neuRef);
      neuBesteht = neuSnap.exists();
      if (neuBesteht && druckerAusSperrdokument(neuSnap) !== printerId) {
        throw new PayfixPrinterIdBelegtError({
          fremd:             false,
          belegenderDrucker: druckerAusSperrdokument(neuSnap),
          belegenderMandant: mandantAusSperrdokument(neuSnap),
        });
      }
    }

    let freizugeben = '';
    if (gespeichert && gespeichert !== neu) {
      const alt = await leseSperreStill(tx, sperrRef(gespeichert));
      if (alt?.exists() && druckerAusSperrdokument(alt) === printerId) freizugeben = gespeichert;
    }

    // ── Schreibsatz ─────────────────────────────────────────────────────────
    if (freizugeben) tx.delete(sperrRef(freizugeben));
    tx.update(printerRef, update);
    // ⚠ `set` UND NICHT `update`: die Rules kennen fuer diese Sammlung kein
    // `update` (REQ-DM-036 Praez. 4 (e)) — ein `set` auf ein bestehendes
    // Dokument waere dort eines und faellt. Geschrieben wird deshalb nur, wenn
    // das Dokument NICHT besteht; genau das heilt den Uebergangsfall.
    if (neuRef && !neuBesteht) tx.set(neuRef, sperrdokumentDaten(printerId, companyId));
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. LOESCHEN — der einzige ECHTE Loeschweg, und er gibt die Kennung frei
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Loescht einen Drucker und raeumt sein Sperrdokument im selben Commit ab
 * (REQ-DM-036 Praez. 4 (d), Nachf. (ii); Akzeptanzkriterium 3b).
 *
 * *Ohne dieses Abraeumen ist die Kennung nach der ersten Loeschung dauerhaft
 * verbrannt, und niemand versteht, warum.*
 *
 * ⚠ ZWEI RANDFAELLE, BEIDE ABSICHT:
 *  • **Ein FEHLENDES Sperrdokument ist KEIN Fehler.** Der Bestand ist bis zur
 *    Bestandswanderung (T-M41.11) nicht gewandert; ein Drucker von vor dem
 *    2026-09-18 hat keines, und seine Loeschung darf daran nicht scheitern.
 *  • **Ein FREMDES Sperrdokument wird nicht angefasst.** Geloescht wird nur,
 *    was im Lesesatz derselben Transaktion gelesen wurde UND auf diesen Drucker
 *    zeigt — sonst gaebe diese Loeschung eine Kennung frei, die inzwischen
 *    einem anderen Kanal gehoert.
 */
export async function loescheDruckerMitSperrdokument(printerId: string): Promise<void> {
  const printerRef = doc(db, 'printers', printerId);

  await runTransaction(db, async (tx) => {
    // ── Lesesatz ────────────────────────────────────────────────────────────
    const printer    = await tx.get(printerRef);
    const schluessel = schluesselOderLeer(printer.data()?.payfixPrinterId);
    let freizugeben  = '';
    if (schluessel) {
      const sperre = await leseSperreStill(tx, sperrRef(schluessel));
      if (sperre?.exists() && druckerAusSperrdokument(sperre) === printerId) {
        freizugeben = schluessel;
      }
    }

    // ── Schreibsatz ─────────────────────────────────────────────────────────
    if (freizugeben) tx.delete(sperrRef(freizugeben));
    tx.delete(printerRef);
  });
}
